import themeVariables from "@/config/theme/themeVariable";
import { useTranslation } from "@/hooks/useTranslation";
import { useLanguage } from "@/hooks/useLanguage";
import { Button, Menu, Select } from "@mantine/core";
import { DollarSign, LayoutList, List, ListFilter, MapIcon, MapPin, Tag, Utensils } from "lucide-react";
import { useAtom } from "jotai";
import { BlueRibbonBadge, MichelinBadge } from "./badges";
import { selectedTabAtom, selectedPriceAtom, selectedCategoryAtom, selectedAwardAtom, viewTypeAtom } from "../_state";

export default function FilterTab({ filters }: { filters: any }) {
  const { t } = useTranslation();
  const { language } = useLanguage();
  const [selectedTab, setSelectedTab] = useAtom(selectedTabAtom);
  const [selectedPrice, setSelectedPrice] = useAtom(selectedPriceAtom);
  const [selectedCategory, setSelectedCategory] = useAtom(selectedCategoryAtom);
  const [selectedAward, setSelectedAward] = useAtom(selectedAwardAtom);
  const [viewType, setViewType] = useAtom(viewTypeAtom);

  const handleTabChange = (tab: string) => {
    setSelectedTab(tab);
  };

  const handlePriceSelect = (price: number) => {
    // 가격 필터 선택 시 다른 필터 초기화
    setSelectedCategory(null);
    setSelectedAward(null);
    setSelectedPrice(selectedPrice === price ? null : price);
  };

  const handleAwardSelect = (award: string) => {
    // 어워드 필터 선택 시 다른 필터 초기화
    setSelectedPrice(null);
    setSelectedCategory(null);
    setSelectedAward(selectedAward === award ? null : award);
  };

  const handleCategorySelect = (category: string | null) => {
    // 카테고리 필터 선택 시 다른 필터 초기화
    setSelectedPrice(null);
    setSelectedAward(null);
    setSelectedCategory(category);
  };

  const AwardLabel = ({ award }: { award: string }) => {
    const awardType = award.split('-')[0];
    const awardValue = award.split('-')[1];

    if (awardType === 'm') {
      return <MichelinBadge award={Number(awardValue)} />
    }
    else if (awardType === 'b') {
      return <BlueRibbonBadge ribbonType={Number(awardValue)} />
    }
  };

  const FilterHeader = () => {
    return (
      <div className="flex items-center mb-6 text-lg font-semibold">
        <div 
          className={`cursor-pointer border-b-2 pb-2 pr-5 pl-2 transition-colors duration-200 ${
            selectedTab === 'price' 
              ? 'border-gray-600 text-gray-800' 
              : 'border-gray-300 text-gray-500'
          }`}
          onClick={() => handleTabChange('price')}
        >
          <div className="flex items-center gap-2.5">
            <DollarSign className="w-[18px] h-[18px]" />
            <div className="h-14 flex items-center text-center md:text-start md:h-auto">{t('filter_by_price')}</div>
          </div>
        </div>
        <div 
          className={`cursor-pointer border-b-2 pb-2 pr-5 pl-2 transition-colors duration-200 ${
            selectedTab === 'category' 
              ? 'border-gray-600 text-gray-800' 
              : 'border-gray-300 text-gray-500'
          }`}
          onClick={() => handleTabChange('category')}
        >
          <div className="flex items-center gap-2.5">
            <Utensils className="w-[18px] h-[18px]" />
            <div className="h-14 flex items-center text-center md:text-start md:h-auto">{t('filter_by_category')}</div>
          </div>
        </div>
        <div 
          className={`cursor-pointer border-b-2 pb-2 pr-5 pl-2 transition-colors duration-200 ${
            selectedTab === 'awards' 
              ? 'border-gray-600 text-gray-800' 
              : 'border-gray-300 text-gray-500'
          }`}
          onClick={() => handleTabChange('awards')}
        >
          <div className="flex items-center gap-2.5">
            <Tag className="w-[18px] h-[18px]" />
            <div className="h-14 flex items-center text-center md:text-start md:h-auto">{t('filter_by_award')}</div>
          </div>
        </div>
      </div>
    )
  }

  const FilterSelect = () => {
    if (selectedTab === 'price') {
      return (
        <div className="flex items-center gap-2 overflow-x-auto scrollbar-hide pb-2 md:flex-wrap md:overflow-visible">
          {filters?.prices?.map((price: number) => (
            <span
              key={price}
              onClick={() => handlePriceSelect(price)}
              className={`cursor-pointer transition-colors duration-200 whitespace-nowrap flex-shrink-0 ${
                selectedPrice === price
                  ? `text-gray-700 text-sm !bg-gray-200 px-2.5 py-1 rounded-md font-semibold`
                  : `text-gray-700 text-sm bg-[#00000008] px-2.5 py-1 rounded-md hover:bg-gray-200`
              }`}
            >
              {Array.from({ length: price }, () => '₩').join('')}
            </span>
          ))}
        </div>
      )
    }
    else if (selectedTab === 'category') {
      const categoryOptions = filters?.categories?.map((category: any) => ({
        value: category.en,
        label: `${category[language] || category.ko} (${category.count})`
      })) || [];

      return (
        <Select
          leftSection={<ListFilter className="w-[18px] h-[18px]" />}
          leftSectionWidth={50}
          className="max-w-[400px]"
          classNames={themeVariables.input.filled}
          placeholder={t('select_cuisine_placeholder')}
          value={selectedCategory}
          onChange={handleCategorySelect}
          data={categoryOptions}
          clearable
        />
      )
    }
    else if (selectedTab === 'awards') {
      return (
        <div className="flex items-center gap-2 overflow-x-auto scrollbar-hide pb-2 md:flex-wrap md:overflow-visible">
          {filters?.awards?.map((award: string) => (
            <span
              key={award}
              onClick={() => handleAwardSelect(award)}
              className={`cursor-pointer transition-colors duration-200 whitespace-nowrap flex-shrink-0 ${
                selectedAward === award
                  ? `text-white text-sm !bg-gray-200 px-2.5 py-1 rounded-md`
                  : `text-gray-700 text-sm bg-[#00000008] px-2.5 py-1 rounded-md hover:bg-gray-200 [&_span]:!font-medium`
              }`}
            >
              <AwardLabel award={award} />
            </span>
          ))}
        </div>
      )
    }
  }
  
  const ViewTypeSelector = () => (
    <div className="w-40 !h-9 mt-8 mb-1">
      <Button
        classNames={{
          label: "!text-gray-600",
          root: "!h-8 rounded-2xl !px-2 !bg-transparent hover:!bg-gray-100",
        }}
        leftSection={viewType === 'list' ? 
          <MapIcon className="w-[18px] h-[18px] text-gray-600" /> : 
          <LayoutList className="w-[18px] h-[18px] text-gray-600" />
        }
        onClick={() => setViewType(viewType === 'list' ? 'map' : 'list')}
      >
        {viewType === 'list' ? t('map_view') : t('list_view')}
      </Button>
    </div>
  )

  return (
    <div className="flex flex-col mt-5 gap-0">
      {/* <div className="border-b border-gray-200 mb-6"></div> */}
      <FilterHeader/>
      <div className="min-h-11">
        <FilterSelect/>
      </div>
      <ViewTypeSelector/>
    </div>
  );
}